import React, { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { Link, useHistory } from "react-router-dom";
import * as yup from "yup";
import { resetPassword } from "../../api/userApi";
import swal from "sweetalert";

const resetSchema = yup.object().shape({
    password: yup
        .string()
        .min(8, "Password must be at least 8 characters")
        .required("New password is required"),
    confirm_password: yup
        .string()
        .oneOf([yup.ref("password"), null], "Passwords don't match")
        .required("Please confirm your password"),
});

export default function ResetPasswordPage() {
    const history = useHistory();
    const [loading, setLoading] = useState(false);
    const token = new URLSearchParams(window.location.search || "").get("token");

    if (!token) {
        return (
            <div className="login-page-wrapper full-page">
                <div className="login-form-card">
                    <div className="login-form-card-body text-center">
                        <i className="fas fa-link-slash text-muted mb-3" style={{ fontSize: "2.5rem" }} />
                        <h2 className="login-title">Invalid or expired link</h2>
                        <p className="login-subtitle text-muted mb-4">
                            This password reset link is invalid or has expired. Please request a new one from the sign-in page.
                        </p>
                        <Link to="/login" className="btn btn-secondary">
                            ← Back to sign in
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="login-page-wrapper full-page">
            <div className="login-form-card">
                <div className="login-form-card-body">
                    <h2 className="login-title">Set new password</h2>
                    <p className="login-subtitle text-muted mb-3">
                        Enter your new password below. It must be at least 8 characters.
                    </p>
                    <Formik
                        initialValues={{ password: "", confirm_password: "" }}
                        validationSchema={resetSchema}
                        onSubmit={async (values, { setSubmitting }) => {
                            setLoading(true);
                            const payload = {
                                reset_token: token,
                                password: values.password,
                                confirm_password: values.confirm_password,
                            };
                            resetPassword(payload)
                                .then((response) => {
                                    setLoading(false);
                                    setSubmitting(false);
                                    swal({
                                        title: "Password changed successfully",
                                        text: "You can now sign in with your new password.",
                                        icon: "success",
                                    }).then(() => {
                                        history.push("/login", { resetSuccess: true });
                                    });
                                })
                                .catch((err) => {
                                    setLoading(false);
                                    setSubmitting(false);
                                    const msg =
                                        err?.response?.data?.errors?.join?.(" ") ||
                                        err?.response?.data?.message ||
                                        "This link may have expired. Please request a new password reset.";
                                    swal({
                                        title: "Could not reset password",
                                        text: msg,
                                        icon: "error",
                                    });
                                });
                        }}
                    >
                        {({ handleSubmit, isSubmitting }) => (
                            <Form className="form-wrap mt-3">
                                <div className="field">
                                    <label>New password</label>
                                    <Field
                                        type="password"
                                        name="password"
                                        className="form-control"
                                        placeholder="At least 8 characters"
                                        autoComplete="new-password"
                                        aria-label="New password"
                                    />
                                    <ErrorMessage name="password" component="div" className="text-danger small" />
                                </div>
                                <div className="field">
                                    <label>Confirm password</label>
                                    <Field
                                        type="password"
                                        name="confirm_password"
                                        className="form-control"
                                        placeholder="Confirm password"
                                        autoComplete="new-password"
                                        aria-label="Confirm password"
                                    />
                                    <ErrorMessage name="confirm_password" component="div" className="text-danger small" />
                                </div>
                                <button
                                    type="submit"
                                    className="btn btn-secondary w-100 my-2"
                                    disabled={isSubmitting || loading}
                                >
                                    {loading ? "Updating…" : "UPDATE PASSWORD"}
                                </button>
                                <p className="text-center mt-3 mb-0">
                                    <Link to="/login" className="btn-link">
                                        ← Back to sign in
                                    </Link>
                                </p>
                            </Form>
                        )}
                    </Formik>
                </div>
            </div>
        </div>
    );
}
